import { useState, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import FilterChipsBar from '#globalComponents/Table/FilterChipsBar';
import { useUrlFilters } from '#globalHooks/useUrlFilters';
import { buildFilterChips, filtersToQueryParams } from '#lib/tableFilters';

import CrmSearchBar from '../../components/common/CrmSearchBar/CrmSearchBar';
import CompanyBulkActions from '../../components/company/CompanyBulkActions/CompanyBulkActions';
import CompanyDrawer from '../../components/company/CompanyDrawer/CompanyDrawer';
import CompanyScopeActions from '../../components/company/CompanyScopeActions/CompanyScopeActions';
import CompanyTable from '../../components/company/CompanyTable/CompanyTable';
import CompanyTableToolbar from '../../components/company/CompanyTableToolbar/CompanyTableToolbar';
import NewCompanyModal from '../../components/company/NewCompanyModal/NewCompanyModal';
import NewContactModal from '../../components/contacts/NewContactModal/NewContactModal';
import ImportLauncher from '../../components/import/ImportLauncher';
import { companiesKeys } from '../../models/companies/keys';
import { useCreateCompany } from '../../models/companies/mutations';
import { useCompaniesList } from '../../models/companies/queries';
import { useTags } from '../../models/tags/queries';
import { buildCompanyFilterSchema } from '../../utils/crmTableFilters';

import { useCompanyRowDetails } from './useCompanyRowDetails';
import { useCompanySelection } from './useCompanySelection';

const PAGE_SIZE = 100;

const CrmCompaniesPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const createCompany = useCreateCompany();

	const [sortKey, setSortKey] = useState('companyName');
	const [sortDir, setSortDir] = useState('asc');
	const [openId, setOpenId] = useState(null);
	const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
	const [showImportWizard, setShowImportWizard] = useState(false);
	const [newContactCompanyId, setNewContactCompanyId] = useState(null);

	const { data: tagsData } = useTags();
	const allTags = useMemo(() => tagsData?.tags ?? [], [tagsData?.tags]);
	const tagOptions = useMemo(() => allTags.map((tag) => ({ value: tag.id, label: tag.name })), [allTags]);
	const filterSchema = useMemo(() => buildCompanyFilterSchema(allTags), [allTags]);
	const { filters, filtersKey, setFilters, removeFilters, clearFilters, page, setPage } = useUrlFilters(filterSchema);

	// contactCount is sorted client-side; the server falls back to companyName.
	const queryFilters = useMemo(
		() => ({
			page,
			pageSize: PAGE_SIZE,
			orderBy: sortKey === 'contactCount' ? 'companyName' : sortKey,
			orderDir: sortDir,
			...filtersToQueryParams(filters),
		}),
		[page, sortKey, sortDir, filters],
	);

	const { data: companiesData, isLoading } = useCompaniesList(queryFilters);
	const companies = useMemo(() => companiesData?.companies ?? [], [companiesData?.companies]);
	const totalCompanies = companiesData?.total ?? 0;
	const totalPages = companiesData?.totalPageCount ?? 1;

	const { dealStatsByCompany, contactCountByCompany, contactsByCompany } = useCompanyRowDetails(companies);

	const sortedCompanies = useMemo(() => {
		if (sortKey !== 'contactCount') return companies;
		return [...companies].sort((a, b) => {
			const ca = contactCountByCompany.get(a.id) ?? 0;
			const cb = contactCountByCompany.get(b.id) ?? 0;
			return sortDir === 'asc' ? ca - cb : cb - ca;
		});
	}, [companies, sortKey, sortDir, contactCountByCompany]);

	const {
		companySelection,
		actionScopeLabel,
		tableSelection,
		contactSelection,
		isContactSelected,
		handleToggleContact,
		expandedCompanyIds,
		handleToggleExpand,
	} = useCompanySelection({
		companies: sortedCompanies,
		totalCount: totalCompanies,
		filters,
		filtersKey,
		contactsByCompany,
	});

	const handleSort = (col) => {
		if (sortKey === col) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(col);
			setSortDir('asc');
		}
		if (col !== 'contactCount') setPage(1);
	};

	const handlePageChange = (next) => setPage(Math.min(Math.max(1, next), totalPages));

	return (
		<Page
			width="full"
			scrollMode="content"
			header={
				<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Unternehmen']}>
					<div className="w-64">
						<CrmSearchBar />
					</div>
				</PageHeader>
			}
		>
			<div className="flex flex-1 min-h-0">
				<div className="flex-1 min-w-0 flex flex-col min-h-0 p-4 lg:p-6">
					<CompanyTableToolbar
						totalCompanies={totalCompanies}
						search={filters.search}
						onSearchChange={(search) => setFilters({ search })}
						onCreateCompany={() => setShowNewCompanyModal(true)}
						onImport={() => setShowImportWizard(true)}
					/>

					<FilterChipsBar
						chips={buildFilterChips(filters, filterSchema)}
						resultLabel={`${totalCompanies.toLocaleString('de-DE')} Unternehmen`}
						onRemove={removeFilters}
						onClearAll={clearFilters}
					/>

					<CompanyTable
						companies={sortedCompanies}
						sortKey={sortKey}
						sortDir={sortDir}
						onSort={handleSort}
						dealStatsByCompany={dealStatsByCompany}
						contactCountByCompany={contactCountByCompany}
						openId={openId}
						isLoading={isLoading}
						onRowClick={(companyId) => setOpenId(openId === companyId ? null : companyId)}
						onAddContact={(companyId) => setNewContactCompanyId(companyId)}
						selection={tableSelection}
						actions={{
							label: actionScopeLabel,
							buttons: (
								<CompanyScopeActions
									companySelection={companySelection}
									contactSelectionCount={contactSelection.selectedIds.length}
									allTags={allTags}
								/>
							),
						}}
						pagination={{
							currentPage: page,
							totalPages,
							onPageChange: handlePageChange,
							totalItems: totalCompanies,
							pageSize: PAGE_SIZE,
						}}
						bulkBar={
							<CompanyBulkActions
								companySelection={companySelection}
								contactSelection={contactSelection}
							/>
						}
						expandedCompanyIds={expandedCompanyIds}
						contactsByCompany={contactsByCompany}
						isContactSelected={isContactSelected}
						onToggleExpand={handleToggleExpand}
						onToggleContact={handleToggleContact}
						tagOptions={tagOptions}
						selectedTagIds={filters.tagIds ?? []}
						onFilterChange={setFilters}
					/>
				</div>

				{openId && (
					<CompanyDrawer
						companyId={openId}
						dealStats={dealStatsByCompany.get(openId)}
						onClose={() => setOpenId(null)}
						onNavigate={() => navigate(`/crm/companies/${openId}`)}
						onAddContact={(companyId) => setNewContactCompanyId(companyId)}
					/>
				)}
			</div>

			{showNewCompanyModal && (
				<NewCompanyModal
					isOpen
					onClose={() => setShowNewCompanyModal(false)}
					onCreated={(payload) => createCompany.mutateAsync(payload)}
				/>
			)}

			{newContactCompanyId && (
				<NewContactModal
					isOpen
					onClose={() => setNewContactCompanyId(null)}
					companyId={newContactCompanyId}
					onCreated={() => {
						queryClient.invalidateQueries({ queryKey: companiesKeys.all });
						setNewContactCompanyId(null);
					}}
				/>
			)}

			<ImportLauncher isOpen={showImportWizard} onClose={() => setShowImportWizard(false)} />
		</Page>
	);
};

export default CrmCompaniesPage;
